import { useQuery } from "react-query";
import axios from "axios";
import toast from "react-hot-toast";
import type { APIResponse, Release, Stats, Config } from "../types";

const api = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL || "/api",
  timeout: 10000,
});

// Error interceptor
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const errorMessage = error.response?.data?.error || "Ocorreu um erro.";
    if (error.response?.status >= 500) {
      toast.error(`Erro no servidor: ${errorMessage}`);
    } else if (error.response?.status === 404) {
      toast.error("Endpoint não encontrado.");
    } else if (error.code === "ECONNABORTED") {
      toast.error("Timeout na requisição. Verifique sua conexão.");
    } else if (errorMessage) {
      toast.error(errorMessage);
    }
    return Promise.reject(error);
  }
);

// Hook to fetch changelog releases
export const useChangelog = (page: number, limit: number, search: string) => {
  return useQuery<APIResponse<Release[]>>(
    ["changelog", page, limit, search],
    async () => {
      const { data } = await api.get<APIResponse<Release[]>>("/changelog", {
        params: { page, limit, search },
      });
      return data;
    },
    {
      keepPreviousData: true,
      onError: () => {
        // The interceptor already handles the error notification
      },
    }
  );
};

// Hook to fetch a single release by ID (cuid) or tagName
export const useChangelogById = (idOrTag: string | undefined) => {
  return useQuery<Release>(
    ["changelog", idOrTag],
    async () => {
      const { data } = await api.get<APIResponse<Release>>(
        `/changelog/${encodeURIComponent(idOrTag!)}`
      );
      return data.data!;
    },
    {
      enabled: !!idOrTag,
      onError: () => undefined,
    }
  );
};

// Hook to fetch statistics
export const useStats = () => {
  return useQuery<Stats>(
    "stats",
    async () => {
      const { data } = await api.get<APIResponse<Stats>>("/stats");
      return data.data!;
    },
    {
      onError: () => {
        // The interceptor already handles the error notification
      },
    }
  );
};

// Hook to fetch configuration
export const useConfig = () => {
  return useQuery<Config>(
    "config",
    async () => {
      const { data } = await api.get<APIResponse<Config>>("/config");
      return data.data!;
    },
    {
      staleTime: 30 * 1000,
      onError: () => undefined,
    }
  );
};
